import { useState, useEffect } from "react";
import { 
    Network, Clock, Search, Activity, CheckCircle2, 
    XCircle, ShieldAlert, Hash
} from "lucide-react";
import baseapi from "../../../../services/baseapi";
import { showError } from "../../../../utils/toast";

export interface InterfaceEntry {
    id: number;
    created: string;
    updated: string;
    is_deleted: boolean;
    agent_id: number;
    client_id: number;
    organization_id: number;
    asset_id: number;
    interface_index: number;
    name: string;
    mac_address: string;
    admin_status: string;
    oper_status: string;
    speed_mbps: number;
}

interface ApInterfacesProps {
    assetId: number;
    client_id?: string | number;
    initialData?: InterfaceEntry[];
    isLoading?: boolean;
}

export default function ApInterfaces({ assetId, client_id, initialData = [], isLoading = true }: ApInterfacesProps) {
    const [data, setData] = useState<InterfaceEntry[]>(initialData);
    const [loading, setLoading] = useState(isLoading);
    const [totalRecords, setTotalRecords] = useState(initialData.length);

    const fetchInterfaces = async () => {
        if (!assetId) return;
        setLoading(true);
        try {
            const params = {
                query: JSON.stringify({ asset_id: assetId, client_id: client_id }),
                skip: 0,
                limit: 100
            };

            const response = await baseapi.get("/ap_interfaces", { params });

            if (response.data.status) {
                const sortedData = [...(response.data.data || [])].sort((a, b) => a.interface_index - b.interface_index);
                setData(sortedData);
                setTotalRecords(response.data.total || sortedData.length);
            } else {
                showError(response.data.message || "Failed to load interfaces");
                setData([]);
                setTotalRecords(0);
            }
        } catch (error: any) {
            showError(error.message || "An error occurred while fetching interfaces");
            setData([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (assetId) {
            fetchInterfaces();
        }
    }, [assetId, client_id]);

    return (
        <div className="w-full flex flex-col gap-4 text-slate-800 dark:text-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                    <div className="bg-indigo-500/10 p-1.5 rounded-md border border-indigo-500/20">
                        <Network size={16} className="text-indigo-500 dark:text-indigo-400" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white tracking-wide leading-tight">Access Point Interfaces</h2>
                        <p className="text-[9px] text-slate-500 dark:text-gray-400 uppercase tracking-widest mt-0.5">Port Configuration & Status</p>
                    </div>
                </div>
                
                <div className="text-[9px] font-bold text-slate-600 dark:text-gray-300 bg-white dark:bg-gray-800/40 px-2 py-1 rounded-md border border-slate-200 dark:border-gray-700">
                    Total Interfaces: <span className="text-gray-900 dark:text-white">{totalRecords}</span>
                </div>
            </div>

            {/* Hardware Switch Visualization */}
            

            {/* Details Table */}
            <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm h-fit w-full">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50 dark:bg-gray-800/80 border-b border-slate-200 dark:border-gray-700/50">
                            <tr>
                                <th className="px-3 py-2 text-[9px] font-bold text-slate-600 dark:text-gray-300 uppercase tracking-widest whitespace-nowrap w-16">
                                    <div className="flex items-center gap-1.5">
                                        <Hash size={12} className="text-slate-400 dark:text-gray-500" /> 
                                        IDX
                                    </div>
                                </th>
                                <th className="px-3 py-2 text-[9px] font-bold text-slate-600 dark:text-gray-300 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-1.5">
                                        <Network size={12} className="text-indigo-500 dark:text-indigo-400" /> 
                                        Interface Name
                                    </div>
                                </th>
                                <th className="px-3 py-2 text-[9px] font-bold text-slate-600 dark:text-gray-300 uppercase tracking-widest whitespace-nowrap">
                                    MAC Address
                                </th>
                                <th className="px-3 py-2 text-[9px] font-bold text-slate-600 dark:text-gray-300 uppercase tracking-widest whitespace-nowrap">
                                    Admin / Oper Status
                                </th>
                                <th className="px-3 py-2 text-[9px] font-bold text-slate-600 dark:text-gray-300 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-1.5">
                                        <Activity size={12} className="text-amber-500 dark:text-amber-400" /> 
                                        Speed
                                    </div>
                                </th>
                                <th className="px-3 py-2 text-[9px] font-bold text-slate-600 dark:text-gray-300 uppercase tracking-widest whitespace-nowrap text-right">
                                    <div className="flex items-center justify-end gap-1.5">
                                        <Clock size={12} className="text-emerald-500 dark:text-emerald-400" /> 
                                        Last Updated
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-gray-700/60 bg-white dark:bg-gray-900/20 text-xs">
                            {loading && data.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-8 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-2">
                                            <div className="w-5 h-5 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                                            <p className="text-[9px] font-black text-slate-500 dark:text-gray-400 uppercase tracking-[0.2em]">Loading Interfaces...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-8 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-2 opacity-40">
                                            <Search size={20} className="text-slate-400 dark:text-gray-400" />
                                            <p className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest">No interfaces found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                data.map((entry) => (
                                    <tr key={entry.id} className="hover:bg-slate-50 dark:hover:bg-gray-800/40 transition-colors group">
                                        <td className="px-3 py-2">
                                            <span className="text-[10px] font-mono font-bold text-slate-600 dark:text-gray-400 bg-slate-100 dark:bg-gray-800/80 px-1.5 py-0.5 rounded border border-slate-200 dark:border-gray-700">
                                                {entry.interface_index < 10 ? `0${entry.interface_index}` : entry.interface_index}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="text-[11px] font-bold text-indigo-600 dark:text-indigo-300">
                                                {entry.name}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="text-[10px] font-mono text-slate-600 dark:text-gray-400">
                                                {entry.mac_address || "-"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-2">
                                                {/* Oper Status */}
                                                {entry.oper_status.toLowerCase() === "up" ? (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-[9px] font-bold uppercase tracking-wider rounded">
                                                        <CheckCircle2 size={10} /> Up
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-400 text-[9px] font-bold uppercase tracking-wider rounded">
                                                        <XCircle size={10} /> Down
                                                    </span>
                                                )}
                                                
                                                {/* Admin Status Warning */}
                                                {entry.admin_status.toLowerCase() === "down" && (
                                                    <span className="text-slate-400 dark:text-gray-500 text-[9px] font-semibold flex items-center gap-0.5 uppercase" title="Administered Down">
                                                        <ShieldAlert size={10} /> Admin Down
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="text-[11px] font-mono font-bold text-slate-700 dark:text-gray-300">
                                                {entry.speed_mbps > 0 ? `${entry.speed_mbps} Mbps` : '—'}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <span className="text-[10px] font-medium text-slate-500 dark:text-gray-400">
                                                {new Date(entry.updated + "Z").toLocaleString()}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}